<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <v-overlay :value="isLoading" z-index="999" :absolute="embedded">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <component :is="embedded ? 'div' : 'v-card'" v-bind="embedded ? {} : { elevation: 6, rounded: 'xxl' }" :class="embedded ? '' : 'app-card'">
        <v-card-title class="mt-4" v-if="!embedded">
          <v-btn class="mx-2" fab x-small color="primary" @click="$router.back()">
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          <span>Edit Assigned Stock</span>
        </v-card-title>
        <component :is="embedded ? 'div' : 'v-card-text'">
          <v-row>
            <!-- Product - Read Only -->
            <v-col sm="4">
              <v-text-field
                :value="getProductName()"
                label="Product"
                outlined
                readonly
                prepend-inner-icon="mdi-package-variant"
                hint="Product cannot be changed"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- Variant - Read Only -->
            <v-col sm="4">
              <v-text-field
                :value="getVariantName()"
                label="Product Variant"
                outlined
                readonly
                prepend-inner-icon="mdi-package-variant-closed"
                hint="Variant cannot be changed"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- Stock Operation Radio Buttons -->
            <v-col cols="12">
              <v-subheader class="px-0">Stock Operation</v-subheader>
              <v-radio-group v-model="stockOperation" row class="mt-0">
                <v-radio
                  label="Direct Update (Set Exact Quantity)"
                  value="direct"
                ></v-radio>
                <v-radio
                  label="Increase Stock"
                  value="increase"
                ></v-radio>
                <v-radio
                  label="Decrease Stock"
                  value="decrease"
                ></v-radio>
              </v-radio-group>
            </v-col>

            <v-col sm="4">
              <v-text-field
                v-model="quantityInput"
                outlined
                :label="getQuantityLabel()"
                type="number"
                min="1"
                prepend-inner-icon="mdi-counter"
                :error-messages="errors.total_quantity"
                :hint="getQuantityHint()"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-select
                v-model="form.assigned_to_id"
                :items="users"
                item-text="display_name"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-account"
                :error-messages="errors.assigned_to_id"
              >
                <template v-slot:label>Assign To <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-select>
            </v-col>

            <!-- Transporter Manager Selection -->
            <v-col sm="4">
              <v-select
                v-model="form.transporter_manager_id"
                :items="transporterManagers"
                item-text="display_name"
                item-value="id"
                label="Transporter Manager *"
                outlined
                prepend-inner-icon="mdi-truck-delivery"
                :error-messages="errors.transporter_manager_id"
                :no-data-text="'No transporter managers available'"
                hint="Select the transporter who will handle delivery"
                persistent-hint
              >
                <template v-slot:prepend-inner>
                  <v-icon color="primary">mdi-truck-delivery</v-icon>
                </template>
              </v-select>
            </v-col>

            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Selected Variant Info -->
          <template v-if="selectedVariant">
            <AppSectionHeader label="Selected Variant Information" />
            <v-row dense>
              <v-col cols="6" sm="3">
                <AppInfoCard icon="mdi-package-variant" label="Product" :value="getProductName()" />
              </v-col>
              <v-col cols="6" sm="3">
                <AppInfoCard icon="mdi-package-variant-closed" label="Variant" :value="`${selectedVariant.color} - ${selectedVariant.size}`" />
              </v-col>
              <v-col cols="6" sm="3">
                <AppInfoCard icon="mdi-currency-inr" label="Price" :value="`₹${selectedVariant.price || '0.00'}`" />
              </v-col>
              <v-col cols="6" sm="3">
                <AppInfoCard icon="mdi-warehouse" label="Company Stock">
                  <v-chip small :color="getCompanyStock() > 0 ? 'success' : 'error'" text-color="white">
                    {{ getCompanyStock() }}
                  </v-chip>
                </AppInfoCard>
              </v-col>
            </v-row>

            <!-- Current vs New Quantity Info -->
            <v-row v-if="old_json" class="mt-1" dense>
              <v-col cols="6" sm="3">
                <AppInfoCard icon="mdi-cube-outline" label="Current Assigned">
                  <v-chip small color="info" text-color="white" class="mono-figure">{{ old_json.total_quantity }}</v-chip>
                </AppInfoCard>
              </v-col>
              <v-col cols="6" sm="3">
                <AppInfoCard icon="mdi-swap-horizontal" :label="stockOperation === 'increase' ? 'Adding' : stockOperation === 'decrease' ? 'Removing' : 'Input'">
                  <v-chip small color="primary" text-color="white" class="mono-figure">{{ quantityInput || 0 }}</v-chip>
                </AppInfoCard>
              </v-col>
              <v-col cols="6" sm="3">
                <AppInfoCard icon="mdi-cube" label="New Quantity">
                  <v-chip small color="warning" text-color="white" class="mono-figure">{{ calculatedNewQuantity }}</v-chip>
                </AppInfoCard>
              </v-col>
              <v-col cols="6" sm="3">
                <AppInfoCard icon="mdi-delta" label="Difference">
                  <v-chip
                    small
                    :color="quantityDifference > 0 ? 'error' : quantityDifference < 0 ? 'success' : 'grey'"
                    text-color="white"
                    class="mono-figure"
                  >
                    {{ quantityDifference > 0 ? '+' : '' }}{{ quantityDifference }}
                  </v-chip>
                </AppInfoCard>
              </v-col>
            </v-row>

            <!-- Insufficient Stock Warning -->
            <v-alert
              v-if="quantityInput && form.variant_id && isInsufficientStock()"
              type="error"
              dense
              class="mt-4"
            >
              <v-icon left>mdi-alert-circle</v-icon>
              <strong>Insufficient Stock!</strong>
              {{ getInsufficientStockMessage() }}
            </v-alert>

            <!-- Decrease Warning -->
            <v-alert
              v-if="stockOperation === 'decrease' && quantityInput > 0 && parseInt(quantityInput) > (old_json ? old_json.total_quantity : 0)"
              type="warning"
              dense
              class="mt-4"
            >
              <v-icon left>mdi-alert</v-icon>
              <strong>Warning!</strong>
              You cannot decrease more than the current assigned quantity ({{ old_json ? old_json.total_quantity : 0 }} units).
            </v-alert>
          </template>

          <v-divider class="mt-5 mb-5"> </v-divider>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading || isInsufficientStock() || isDecreaseExceeded()"
            color="primary"
            rounded
            dark
            @click="save"
          >
            Update Assigned Stock
          </v-btn>
        </component>
      </component>
    </component>
  </component>
</template>

<script>
import axios from "axios";
import AppInfoCard from "@/components/AppInfoCard.vue";
import AppSectionHeader from "@/components/AppSectionHeader.vue";

export default {
  name: "AssignedStockEditPage",
  components: { AppInfoCard, AppSectionHeader },
  props: {
    // When true, this renders inside a dialog (opened from the Assigned
    // Stocks list on row click) instead of its own routed page - the id
    // comes from the `assignedStockId` prop rather than the route, and
    // "back"/save close the dialog instead of navigating.
    embedded: { type: Boolean, default: false },
    assignedStockId: { type: String, default: null },
  },
  data() {
    return {
      form: {},
      products: [],
      variants: [],
      users: [],
      transporterManagers: [],
      selectedVariant: null,
      companyStocks: [],
      companyStockMap: {},
      isLoading: false,
      old_json: null,
      stockOperation: 'direct', // 'direct', 'increase', 'decrease'
      quantityInput: null,
    };
  },
  computed: {
    errors() {
      return this.$store.getters['errors/getErrors'] || {};
    },
    user() {
      return this.$store.getters['auth/getMe'];
    },
    filteredProducts() {
      if (!this.products || this.products.length === 0) return [];

      return this.products.filter(product => {
        return product.product_variants && product.product_variants.length > 0;
      }).map(product => {
        const variantCount = product.product_variants.length;
        return {
          ...product,
          name: `${product.name} (${variantCount > 1 ? variantCount + ' variants' : '1 variant'})`
        };
      });
    },
    calculatedNewQuantity() {
      const currentQty = this.old_json ? parseInt(this.old_json.total_quantity) || 0 : 0;
      const inputQty = parseInt(this.quantityInput) || 0;

      switch (this.stockOperation) {
        case 'increase':
          return currentQty + inputQty;
        case 'decrease':
          return Math.max(0, currentQty - inputQty);
        case 'direct':
        default:
          return inputQty;
      }
    },
    quantityDifference() {
      const currentQty = this.old_json ? parseInt(this.old_json.total_quantity) || 0 : 0;
      return this.calculatedNewQuantity - currentQty;
    }
  },
  async mounted() {
    this.$store.dispatch('errors/setErrors', {});
    // Load masters and company stocks first, then load form data
    await Promise.all([this.fetchMasters(), this.getCompanyStocks()]);
    await this.getData();
  },
  methods: {
    async fetchMasters() {
      try {
        // Single API call to get all dropdown data
        const response = await axios.get("/masters", {
          params: { context: "assign_stock" }
        });

        const data = response.data;

        // Products with variants - transform to match expected structure
        this.products = (data.products_with_variants || []).map(product => ({
          id: product.id,
          name: product.name,
          description: product.description,
          product_variants: (product.variants || []).map(v => ({
            id: v.id,
            product_id: v.product_id,
            color: v.color,
            size: v.size,
            price: v.price,
            discount_percentage: v.discount_percentage,
            discount_amount: v.discount_amount,
            available_quantity: v.available_quantity,
          }))
        }));

        // Combine distributors, franchises, and sale executives into users
        const distributors = (data.distributors || []).map(user => ({
          id: user.id,
          first_name: user.name.split(' ')[0],
          last_name: user.name.split(' ').slice(1).join(' '),
          phone: user.phone,
          email: user.email,
          position: { name: user.position },
          display_name: `${user.name} (${user.email || user.phone || 'N/A'})`
        }));

        const franchises = (data.franchises || []).map(user => ({
          id: user.id,
          first_name: user.name.split(' ')[0],
          last_name: user.name.split(' ').slice(1).join(' '),
          phone: user.phone,
          email: user.email,
          position: { name: user.position },
          display_name: `${user.name} (${user.email || user.phone || 'N/A'})`
        }));

        const saleExecutives = (data.sale_executives || []).map(user => ({
          id: user.id,
          first_name: user.name.split(' ')[0],
          last_name: user.name.split(' ').slice(1).join(' '),
          phone: user.phone,
          email: user.email,
          position: { name: user.position },
          display_name: `${user.name} (${user.email || user.phone || 'N/A'})`
        }));

        this.users = [...distributors, ...franchises, ...saleExecutives];

        // Transporter managers
        this.transporterManagers = (data.transporter_managers || []).map(user => ({
          id: user.id,
          first_name: user.name.split(' ')[0],
          last_name: user.name.split(' ').slice(1).join(' '),
          phone: user.phone,
          email: user.email,
          display_name: `${user.name} (${user.phone || 'N/A'})`
        }));

      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },
    formatVariantName(variant) {
      let name = [];
      if (variant.color) name.push(`Color: ${variant.color}`);
      if (variant.size) name.push(`Size: ${variant.size}`);
      if (variant.price) name.push(`₹${variant.price}`);
      return name.length > 0 ? name.join(" | ") : "Variant";
    },
    async getCompanyStocks() {
      try {
        const response = await axios.get("/company_stocks");
        this.companyStocks = response.data.data || [];

        // Create a map for quick lookup of company stock by variant_id
        this.companyStockMap = {};
        this.companyStocks.forEach(stock => {
          if (stock.variant_id) {
            this.companyStockMap[stock.variant_id] = stock;
          }
        });
      } catch (error) {
        console.error("Error fetching company stocks:", error);
      }
    },
    getProductName() {
      if (!this.form.product_id) return "";
      // First try to get from loaded products list
      const product = this.products.find(p => p.id === this.form.product_id);
      if (product) return product.name;
      // Fallback to product data from API response
      if (this.form.product) return this.form.product.name;
      return "";
    },
    getVariantName() {
      if (!this.selectedVariant) return "";
      let name = [];
      if (this.selectedVariant.color) name.push(`Color: ${this.selectedVariant.color}`);
      if (this.selectedVariant.size) name.push(`Size: ${this.selectedVariant.size}`);
      if (this.selectedVariant.price) name.push(`₹${this.selectedVariant.price}`);
      return name.length > 0 ? name.join(" | ") : "N/A";
    },
    getCompanyStock() {
      if (!this.form.variant_id) return 0;
      const stock = this.companyStockMap[this.form.variant_id];
      return stock ? parseInt(stock.quantity) || 0 : 0;
    },
    getQuantityLabel() {
      switch (this.stockOperation) {
        case 'increase':
          return 'Quantity to Add *';
        case 'decrease':
          return 'Quantity to Remove *';
        case 'direct':
        default:
          return 'New Quantity *';
      }
    },
    getQuantityHint() {
      const currentQty = this.old_json ? this.old_json.total_quantity : 0;
      switch (this.stockOperation) {
        case 'increase':
          return `Current: ${currentQty}. Enter amount to add.`;
        case 'decrease':
          return `Current: ${currentQty}. Enter amount to remove.`;
        case 'direct':
        default:
          return `Current: ${currentQty}. Enter new total quantity.`;
      }
    },
    getInsufficientStockMessage() {
      const companyStock = this.getCompanyStock();
      const currentAssigned = this.old_json ? this.old_json.total_quantity : 0;

      if (this.stockOperation === 'increase') {
        const increaseAmount = parseInt(this.quantityInput) || 0;
        return `You want to add ${increaseAmount} units, but only ${companyStock} units are available in company stock.`;
      } else if (this.stockOperation === 'direct') {
        const newQty = parseInt(this.quantityInput) || 0;
        const diff = newQty - currentAssigned;
        if (diff > 0) {
          return `You need ${diff} additional units, but only ${companyStock} units are available in company stock.`;
        }
      }
      return `Insufficient stock available. Company stock: ${companyStock} units.`;
    },
    isInsufficientStock() {
      if (!this.quantityInput || !this.form.variant_id) return false;

      const companyStock = this.getCompanyStock();
      const currentAssigned = this.old_json ? parseInt(this.old_json.total_quantity) || 0 : 0;

      // Calculate how much additional stock is needed from company
      let additionalNeeded = 0;

      switch (this.stockOperation) {
        case 'increase':
          additionalNeeded = parseInt(this.quantityInput) || 0;
          break;
        case 'decrease':
          // Decreasing doesn't need company stock
          return false;
        case 'direct':
        default: {
          const newQty = parseInt(this.quantityInput) || 0;
          additionalNeeded = Math.max(0, newQty - currentAssigned);
          break;
        }
      }

      return additionalNeeded > companyStock;
    },
    isDecreaseExceeded() {
      if (this.stockOperation !== 'decrease') return false;
      const currentAssigned = this.old_json ? parseInt(this.old_json.total_quantity) || 0 : 0;
      const decreaseAmount = parseInt(this.quantityInput) || 0;
      return decreaseAmount > currentAssigned;
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.assignedStockId || this.$route.params.assignedStockId;
      try {
        const response = await axios.get(`/assigned_stocks/${encryptedId}`);
        this.form = response.data.data;
        this.old_json = JSON.parse(JSON.stringify(response.data.data)); // Deep copy

        // Initialize quantityInput with current quantity for direct mode
        this.quantityInput = this.form.total_quantity;

        // Use variant data directly from API response (it includes variant relation)
        if (this.form.variant) {
          this.selectedVariant = this.form.variant;
        }

        // Load variants for existing product (for reference)
        if (this.form.product_id) {
          await this.loadVariantsForProduct(this.form.product_id);
          // If selectedVariant not set from API, try to find it in loaded variants
          if (!this.selectedVariant && this.form.variant_id) {
            this.selectedVariant = this.variants.find(v => v.id === this.form.variant_id);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async loadVariantsForProduct(productId) {
      const product = this.products.find(p => p.id === productId);
      if (product && product.product_variants) {
        this.variants = product.product_variants;
      }
    },
    async save() {
      try {
        this.isLoading = true;
        this.$store.dispatch('errors/setErrors', {});

        // Validation
        let validationErrors = {};
        if (!this.quantityInput || parseInt(this.quantityInput) < 1) {
          validationErrors.total_quantity = ["Quantity is required and must be at least 1"];
        }
        if (this.calculatedNewQuantity < 1) {
          validationErrors.total_quantity = ["Final quantity must be at least 1"];
        }
        if (!this.form.assigned_to_id) {
          validationErrors.assigned_to_id = ["Please select a user to assign to"];
        }
        if (!this.form.transporter_manager_id) {
          validationErrors.transporter_manager_id = ["Please select a transporter manager"];
        }
        if (this.isDecreaseExceeded()) {
          validationErrors.total_quantity = ["Cannot decrease more than current assigned quantity"];
        }

        if (Object.keys(validationErrors).length > 0) {
          this.$store.dispatch('errors/setErrors', validationErrors);
          this.isLoading = false;
          return;
        }

        // Set the calculated quantity to form before sending
        this.form.total_quantity = this.calculatedNewQuantity;

        const targetId = this.assignedStockId || this.$route.params.assignedStockId;
        await axios
          .patch(`/assigned_stocks/${targetId}`, this.form)
          .then(async () => {
            await this.saveUserTimestamp(this.old_json, this.form);
          })
          .catch((error) => {
            console.error("Error updating assigned stock:", error);

            if (error.response && error.response.data) {
              // Handle insufficient stock error specifically
              if (error.response.data.title === 'Insufficient Stock') {
                if (this.$toast) {
                  this.$toast.error(error.response.data.sub_title || 'Insufficient stock available');
                }
              }

              // Set validation errors if available
              if (error.response.data.data) {
                this.$store.dispatch('errors/setErrors', error.response.data.data);
              }
            }
            throw error;
          });
        this.isLoading = false;
        if (this.embedded) {
          this.$emit("saved");
          this.$emit("close");
        } else {
          this.$router.push("/assigned_stocks");
        }
      } catch (e) {
        this.isLoading = false;
      }
    },
    async saveUserTimestamp() {
      try {
        let form = {
          from_path: `/assigned_stocks/${this.form.id}`,
          from_name: "AssignedStockEditPage",
          user_id: this.user.id,
          old_json: JSON.stringify(this.old_json),
          new_json: JSON.stringify(this.form),
        };
        await axios.post(`/user_timestamps`, form);
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped></style>